(() => {
  document.documentElement.classList.add('js');

  // Liquid owns translations; runtime code only interpolates changing values.
  const translationNode = document.querySelector('[data-cw-i18n]');
  let translations = {};
  if (translationNode) {
    try {
      translations = JSON.parse(translationNode.textContent || '{}');
    } catch (error) {
      translations = {};
    }
  }
  window.cwI18n = (key, values = {}) => {
    let text = translations[key] || '';
    Object.entries(values).forEach(([name, value]) => {
      text = text.replaceAll(`__${name.toUpperCase()}__`, String(value));
    });
    return text;
  };

  // Shared selectors for global drawers and reveal effects.
  const selectors = {
    reveal: '[data-cw-reveal]',
    menu: '[data-cw-mobile-nav]',
    menuOpen: '[data-cw-menu-open]',
    menuClose: '[data-cw-menu-close]',
    scrim: '[data-cw-scrim]',
    search: '[data-cw-search]',
    searchOpen: '[data-cw-search-open]',
    searchClose: '[data-cw-search-close]',
    background: '[data-cw-background]',
  };

  let lastFocusedElement = null;
  const announcementStates = new Map();
  const headerStates = new Map();
  const footerMotionStates = new Map();
  let typingObserver = null;

  const readMotionValue = (property, fallback) => {
    const value = Number.parseFloat(
      window.getComputedStyle(document.documentElement).getPropertyValue(property)
    );
    return Number.isFinite(value) ? value : fallback;
  };

  // Focus and scroll helpers keep the mobile menu and search modal accessible.
  const focusableElements = (container) =>
    [...container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )].filter((element) => !element.hidden && element.offsetParent !== null);

  const setScrollLock = (locked) => {
    document.body.classList.toggle('cw-lock-scroll', locked);
  };

  const setBackgroundInert = (locked) => {
    document.querySelectorAll(selectors.background).forEach((region) => {
      region.inert = locked;
    });
  };

  const trapFocus = (event, container) => {
    if (event.key !== 'Tab') return;
    const focusable = focusableElements(container);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const setExpanded = (triggerSelector, expanded) => {
    document.querySelectorAll(triggerSelector).forEach((trigger) => {
      trigger.setAttribute('aria-expanded', String(expanded));
    });
  };

  const closeNavDetails = ({ restoreFocus = false } = {}) => {
    document.querySelectorAll('.cw-nav-details[open]').forEach((details) => {
      const summary = details.querySelector(':scope > summary');
      details.removeAttribute('open');
      if (restoreFocus && summary instanceof HTMLElement) summary.focus();
    });
  };

  const closeOtherNavDetails = (currentDetails) => {
    document.querySelectorAll('.cw-nav-details[open]').forEach((details) => {
      if (details !== currentDetails) details.removeAttribute('open');
    });
  };

  const showStickyHeader = () => {
    document.querySelectorAll('.cw-header-section.is-sticky').forEach((wrapper) => {
      wrapper.classList.remove('is-scroll-hidden');
      const state = headerStates.get(wrapper);
      if (!state) return;

      const currentY = Math.max(0, window.scrollY);
      state.directionStart = currentY;
      state.lastDirection = 0;
      state.lastY = currentY;
      state.stateChangeLockUntil = performance.now() + state.stateChangeLockDuration;
    });
  };

  const destroyHeader = (scope = document) => {
    const header = scope.matches?.('.cw-site-header')
      ? scope
      : scope.querySelector?.('.cw-site-header');
    const wrapper = header?.closest('.cw-header-section');
    const state = wrapper ? headerStates.get(wrapper) : null;
    state?.controller.abort();
    if (wrapper) {
      headerStates.delete(wrapper);
      wrapper.classList.remove('is-scroll-hidden', 'is-scrolled');
    }
  };

  // Keep the header sticky, hiding it on downward scroll and revealing it on upward scroll.
  const initHeader = (scope = document) => {
    const header = scope.matches?.('.cw-site-header')
      ? scope
      : scope.querySelector?.('.cw-site-header') || document.querySelector('.cw-site-header');
    const wrapper = header?.closest('.cw-header-section');
    if (!header || !wrapper) return;

    const existingState = headerStates.get(wrapper);
    existingState?.controller.abort();
    headerStates.delete(wrapper);

    const isSticky = header.dataset.sticky === 'true';
    const hidesOnScroll = isSticky && header.dataset.hideOnScroll === 'true';
    wrapper.classList.toggle('is-sticky', isSticky);
    wrapper.classList.remove('is-scroll-hidden');

    if (!hidesOnScroll) return;

    const syncHeaderHeight = () => {
      wrapper.style.setProperty('--cw-header-runtime-height', `${Math.ceil(wrapper.offsetHeight)}px`);
    };
    syncHeaderHeight();

    const controller = new AbortController();
    const state = {
      controller,
      directionStart: Math.max(0, window.scrollY),
      lastDirection: 0,
      lastY: Math.max(0, window.scrollY),
      stateChangeLockDuration: 420,
      stateChangeLockUntil: 0,
      ticking: false,
    };
    headerStates.set(wrapper, state);

    const setScrollHidden = (hidden, currentY) => {
      const isHidden = wrapper.classList.contains('is-scroll-hidden');
      if (isHidden === hidden) return false;

      wrapper.classList.toggle('is-scroll-hidden', hidden);
      state.directionStart = currentY;
      state.lastDirection = 0;
      state.lastY = currentY;
      state.stateChangeLockUntil = performance.now() + state.stateChangeLockDuration;
      return true;
    };

    const update = () => {
      const currentY = Math.max(0, window.scrollY);
      const delta = currentY - state.lastY;
      const direction = delta > 0 ? 1 : delta < 0 ? -1 : state.lastDirection;
      const drawerOpen =
        document.querySelector(`${selectors.menu}.is-open`) ||
        document.querySelector(`${selectors.search}.is-open`);
      const headerHasFocus = wrapper.contains(document.activeElement);

      wrapper.classList.toggle('is-scrolled', currentY > 8);

      if (currentY <= 12 || drawerOpen || headerHasFocus) {
        if (setScrollHidden(false, currentY)) {
          state.ticking = false;
          return;
        }
        state.directionStart = currentY;
      } else if (state.stateChangeLockUntil > performance.now()) {
        state.directionStart = currentY;
        state.lastDirection = 0;
        state.lastY = currentY;
        state.ticking = false;
        return;
      } else if (direction !== state.lastDirection) {
        state.directionStart = currentY;
      } else {
        const directionDistance = Math.abs(currentY - state.directionStart);
        const beyondHeader = currentY > Math.max(header.offsetHeight + 32, 120);

        if (direction < 0 && directionDistance >= 8) {
          if (setScrollHidden(false, currentY)) {
            state.ticking = false;
            return;
          }
        } else if (direction > 0 && directionDistance >= 18 && beyondHeader) {
          closeNavDetails();
          if (setScrollHidden(true, currentY)) {
            state.ticking = false;
            return;
          }
        }
      }

      state.lastDirection = direction;
      state.lastY = currentY;
      state.ticking = false;
    };

    const requestUpdate = () => {
      if (state.ticking) return;
      state.ticking = true;
      window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', requestUpdate, {
      passive: true,
      signal: controller.signal,
    });
    window.addEventListener('resize', () => {
      syncHeaderHeight();
      requestUpdate();
    }, {
      passive: true,
      signal: controller.signal,
    });
    wrapper.addEventListener('focusin', showStickyHeader, {
      signal: controller.signal,
    });
    update();
  };

  // Mobile navigation drawer.
  const closeMenu = ({ restoreFocus = true } = {}) => {
    const menu = document.querySelector(selectors.menu);
    const scrim = document.querySelector(selectors.scrim);
    if (!menu) return;

    closeNavDetails();
    menu.classList.remove('is-open');
    menu.setAttribute('aria-hidden', 'true');
    menu.setAttribute('inert', '');
    scrim?.classList.remove('is-visible');
    setExpanded(selectors.menuOpen, false);
    const searchIsOpen = document.querySelector(selectors.search)?.classList.contains('is-open');
    setScrollLock(searchIsOpen);
    setBackgroundInert(searchIsOpen);

    if (restoreFocus && lastFocusedElement instanceof HTMLElement) lastFocusedElement.focus();
  };

  const openMenu = (trigger) => {
    const menu = document.querySelector(selectors.menu);
    const scrim = document.querySelector(selectors.scrim);
    if (!menu) return;

    showStickyHeader();
    closeSearch({ restoreFocus: false });
    lastFocusedElement = trigger;
    menu.removeAttribute('inert');
    menu.classList.add('is-open');
    menu.setAttribute('aria-hidden', 'false');
    scrim?.classList.add('is-visible');
    setExpanded(selectors.menuOpen, true);
    setScrollLock(true);
    setBackgroundInert(true);
    focusableElements(menu)[0]?.focus();
  };

  // Search modal.
  const closeSearch = ({ restoreFocus = true } = {}) => {
    const search = document.querySelector(selectors.search);
    if (!search) return;

    search.classList.remove('is-open');
    search.setAttribute('aria-hidden', 'true');
    search.setAttribute('inert', '');
    setExpanded(selectors.searchOpen, false);
    const menuIsOpen = document.querySelector(selectors.menu)?.classList.contains('is-open');
    setScrollLock(menuIsOpen);
    setBackgroundInert(menuIsOpen);

    if (restoreFocus && lastFocusedElement instanceof HTMLElement) lastFocusedElement.focus();
  };

  const openSearch = (trigger) => {
    const search = document.querySelector(selectors.search);
    if (!search) return;

    showStickyHeader();
    closeMenu({ restoreFocus: false });
    lastFocusedElement = trigger;
    search.removeAttribute('inert');
    search.classList.add('is-open');
    search.setAttribute('aria-hidden', 'false');
    setExpanded(selectors.searchOpen, true);
    setScrollLock(true);
    setBackgroundInert(true);
    search.querySelector('input[type="search"]')?.focus();
  };

  // Progressive reveal utility shared by all page workstreams with safety fallbacks.
  const initReveal = (scope = document) => {
    // Auto-stagger indexing for container elements marked with data-reveal-group or data-stagger
    scope.querySelectorAll?.('[data-reveal-group], [data-stagger]').forEach((group) => {
      if (group.dataset.cwStaggerReady) return;
      group.dataset.cwStaggerReady = 'true';
      const items = [...group.children].filter((child) => child.nodeType === 1);
      items.forEach((item, index) => {
        if (
          !item.hasAttribute('data-stagger-item') &&
          !item.hasAttribute('data-cw-stagger-item') &&
          !item.hasAttribute('data-cwh-stagger-item') &&
          !item.hasAttribute('data-reveal') &&
          !item.hasAttribute('data-cw-reveal') &&
          !item.hasAttribute('data-cwh-reveal')
        ) {
          item.setAttribute('data-stagger-item', 'true');
        }
        if (!item.style.getPropertyValue('--stagger-index')) {
          item.style.setProperty('--stagger-index', String(Math.min(index, 12)));
        }
      });
    });

    const selectorList =
      '[data-reveal], [data-cw-reveal], [data-cwh-reveal], [data-stagger-item], [data-cw-stagger-item], [data-cwh-stagger-item]';
    const reveals = [...(scope.querySelectorAll?.(selectorList) || [])].filter(
      (element) => !element.dataset.cwRevealReady
    );
    if (!reveals.length) return;

    const revealElement = (element) => {
      element.classList.add('is-visible');
      if (element.matches?.('[data-cwh-motion-section]')) {
        element.classList.add('is-cwh-inview');
      }
      delete element.dataset.cwRevealActive;
    };

    const forceRevealAll = () => {
      reveals.forEach(revealElement);
    };

    if (
      document.body?.dataset.cwMotion === 'false' ||
      document.body?.dataset.cwRevealEnabled === 'false' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      !('IntersectionObserver' in window)
    ) {
      forceRevealAll();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          revealElement(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '220px 0px 220px 0px', threshold: 0.05 }
    );

    let revealTimer = 0;
    const revealNearbyContent = () => {
      if (revealTimer) return;
      const run = () => {
        revealTimer = 0;
        const viewportTop = -220;
        const viewportBottom = window.innerHeight + 220;
        reveals.forEach((element) => {
          if (element.classList.contains('is-visible')) return;
          const rect = element.getBoundingClientRect();
          if (rect.top <= viewportBottom && rect.bottom >= viewportTop) {
            revealElement(element);
            observer.unobserve(element);
          }
        });
      };
      revealTimer = window.setTimeout(run, 16);
    };

    reveals.forEach((element) => {
      element.dataset.cwRevealReady = 'true';
      element.dataset.cwRevealActive = 'true';
      observer.observe(element);
    });

    // Fast scrollbar jumps can skip an IntersectionObserver entry entirely.
    // Once content has passed above the viewport it should never remain hidden.
    window.addEventListener('scroll', revealNearbyContent, { passive: true });
    window.addEventListener('resize', revealNearbyContent, { passive: true });
    revealNearbyContent();

  };

  // Editorial pages do not load the homepage motion bundle, so they need their
  // own heading treatment. Keep the original text in aria-label and reveal
  // characters progressively when each heading reaches the viewport.
  const prepareTypingHeading = (heading) => {
    if (!heading || heading.dataset.cwhTypeReady || heading.children.length) return;

    const accessibleLabel = heading.textContent.replace(/\s+/g, ' ').trim();
    if (!accessibleLabel) return;

    let characterIndex = 0;
    const walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) =>
        node.textContent.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT,
    });
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);

    textNodes.forEach((textNode) => {
      const fragment = document.createDocumentFragment();
      textNode.textContent.split(/(\s+)/).forEach((part) => {
        if (!part) return;
        if (/^\s+$/.test(part)) {
          fragment.appendChild(document.createTextNode(part));
          return;
        }

        const word = document.createElement('span');
        word.className = 'cwh-type-word';
        word.setAttribute('aria-hidden', 'true');
        [...part].forEach((character) => {
          const characterNode = document.createElement('span');
          characterNode.className = 'cwh-type-char';
          characterNode.style.setProperty('--cwh-character-index', String(characterIndex));
          characterNode.textContent = character;
          word.appendChild(characterNode);
          characterIndex += 1;
        });
        fragment.appendChild(word);
      });
      textNode.replaceWith(fragment);
    });

    heading.dataset.cwhTypeReady = 'true';
    heading.dataset.cwhCharacterCount = String(characterIndex);
    heading.classList.add('cwh-type-heading');
    heading.setAttribute('aria-label', accessibleLabel);
  };

  const finishTypingHeading = (heading) => {
    if (!heading || heading.classList.contains('is-cwh-typing-started')) return;
    const characterCount = Number(heading.dataset.cwhCharacterCount || 1);
    heading.classList.add('is-cwh-typing-started');
    const characterDelay = readMotionValue('--cw-typing-delay', 45);
    window.setTimeout(
      () => heading.classList.add('is-cwh-typed'),
      Math.min(4200, characterCount * characterDelay + 300)
    );
  };

  const initTyping = (scope = document) => {
    const headings = [...(scope.querySelectorAll?.('main h1, main h2') || [])].filter(
      (heading) => !heading.closest('[hidden]') && !heading.hasAttribute('data-cw-no-typing')
    );
    if (!headings.length) return;

    headings.forEach(prepareTypingHeading);
    const readyHeadings = headings.filter((heading) => heading.dataset.cwhTypeReady);
    if (!readyHeadings.length) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (
      document.body?.dataset.cwMotion === 'false' ||
      document.body?.dataset.cwTyping === 'false' ||
      reduced ||
      !('IntersectionObserver' in window)
    ) {
      readyHeadings.forEach(finishTypingHeading);
      return;
    }

    document.documentElement.classList.add('cw-typing-motion');
    if (!typingObserver) {
      typingObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            finishTypingHeading(entry.target);
            typingObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -16% 0px', threshold: 0.16 }
      );
    }

    readyHeadings.forEach((heading) => {
      if (heading.dataset.cwhTypeObserved) return;
      heading.dataset.cwhTypeObserved = 'true';
      typingObserver.observe(heading);
    });

    let typingTimer = 0;
    const typeVisibleHeadings = () => {
      if (typingTimer) return;
      typingTimer = window.setTimeout(() => {
        typingTimer = 0;
        const viewportTop = -120;
        const viewportBottom = window.innerHeight + 120;
        readyHeadings.forEach((heading) => {
          if (heading.classList.contains('is-cwh-typing-started')) return;
          const rect = heading.getBoundingClientRect();
          if (rect.top <= viewportBottom && rect.bottom >= viewportTop) {
            finishTypingHeading(heading);
          }
        });
      }, 16);
    };

    window.addEventListener('scroll', typeVisibleHeadings, { passive: true });
    window.addEventListener('resize', typeVisibleHeadings, { passive: true });
    typeVisibleHeadings();
  };

  // Footer motion uses its own viewport timing so it does not finish while the
  // customer is still near the top of a long page.
  const getFooters = (scope = document) => {
    const footers = [...(scope.querySelectorAll?.('[data-cw-footer-motion]') || [])];
    if (scope.matches?.('[data-cw-footer-motion]')) footers.unshift(scope);
    return [...new Set(footers)];
  };

  const revealFooter = (footer) => {
    footer.classList.add('is-cw-footer-inview');
    const state = footerMotionStates.get(footer);
    state?.observer.disconnect();
    footerMotionStates.delete(footer);
  };

  const destroyFooterMotion = (scope = document) => {
    getFooters(scope).forEach((footer) => {
      footerMotionStates.get(footer)?.observer.disconnect();
      footerMotionStates.delete(footer);
      delete footer.dataset.cwFooterMotionReady;
    });
  };

  const initFooterMotion = (scope = document) => {
    getFooters(scope).forEach((footer) => {
      if (footer.dataset.cwFooterMotionReady) return;
      footer.dataset.cwFooterMotionReady = 'true';

      footer.querySelectorAll('[data-cw-footer-item]').forEach((item, index) => {
        item.style.setProperty('--cw-footer-motion-index', String(Math.min(index, 8)));
      });

      const shouldRevealImmediately =
        window.Shopify?.designMode ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
        !('IntersectionObserver' in window);

      if (shouldRevealImmediately) {
        revealFooter(footer);
        return;
      }

      document.documentElement.classList.add('cw-footer-motion-ready');
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) revealFooter(entry.target);
          });
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0.08 }
      );

      footerMotionStates.set(footer, { observer });
      observer.observe(footer);
    });
  };

  // Rotate announcements, with pause controls and Theme Editor-safe cleanup.
  const startAnnouncement = (region) => {
    const state = announcementStates.get(region);
    if (!state || state.timer || state.paused.size || document.hidden) return;

    state.timer = window.setInterval(() => {
      state.items[state.activeIndex].setAttribute('aria-hidden', 'true');
      state.activeIndex = (state.activeIndex + 1) % state.items.length;
      state.items[state.activeIndex].setAttribute('aria-hidden', 'false');
    }, readMotionValue('--cw-announcement-interval', 5) * 1000);
  };

  const stopAnnouncement = (region) => {
    const state = announcementStates.get(region);
    if (!state?.timer) return;
    window.clearInterval(state.timer);
    state.timer = null;
  };

  const setAnnouncementPause = (region, reason, paused) => {
    const state = announcementStates.get(region);
    if (!state) return;
    if (paused) state.paused.add(reason);
    else state.paused.delete(reason);

    if (state.paused.size) stopAnnouncement(region);
    else startAnnouncement(region);
  };

  const destroyAnnouncements = (scope) => {
    scope.querySelectorAll?.('[data-cw-announcements]').forEach((region) => {
      const state = announcementStates.get(region);
      stopAnnouncement(region);
      state?.controller.abort();
      announcementStates.delete(region);
      delete region.dataset.cwReady;
    });
  };

  const initAnnouncements = (scope = document) => {
    scope.querySelectorAll('[data-cw-announcements]').forEach((region) => {
      if (region.dataset.cwReady) return;
      region.dataset.cwReady = 'true';

      const items = [...region.querySelectorAll('[data-cw-announcement]')];
      if (items.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const controller = new AbortController();
      announcementStates.set(region, {
        activeIndex: Math.max(0, items.findIndex((item) => item.getAttribute('aria-hidden') === 'false')),
        controller,
        items,
        paused: new Set(),
        timer: null,
      });

      region.addEventListener('mouseenter', () => setAnnouncementPause(region, 'hover', true), {
        signal: controller.signal,
      });
      region.addEventListener('mouseleave', () => setAnnouncementPause(region, 'hover', false), {
        signal: controller.signal,
      });
      region.addEventListener('focusin', () => setAnnouncementPause(region, 'focus', true), {
        signal: controller.signal,
      });
      region.addEventListener('focusout', (event) => {
        if (!region.contains(event.relatedTarget)) setAnnouncementPause(region, 'focus', false);
      }, { signal: controller.signal });

      startAnnouncement(region);
    });
  };

  // Delegated events also support sections re-rendered by the theme editor.
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const menuOpen = target.closest(selectors.menuOpen);
    const menuClose = target.closest(selectors.menuClose);
    const searchOpen = target.closest(selectors.searchOpen);
    const searchClose = target.closest(selectors.searchClose);
    const navSummary = target.closest('.cw-nav-details > summary');
    const insideNavDetails = target.closest('.cw-nav-details');

    if (navSummary) closeOtherNavDetails(navSummary.parentElement);
    else if (!insideNavDetails) closeNavDetails();

    if (menuOpen) openMenu(menuOpen);
    if (menuClose || target.matches(selectors.scrim)) closeMenu();
    if (searchOpen) openSearch(searchOpen);
    if (searchClose || target.matches(selectors.search)) {
      closeSearch();
    }
  });

  document.addEventListener('keydown', (event) => {
    const openMenuElement = document.querySelector(`${selectors.menu}.is-open`);
    const openSearchElement = document.querySelector(`${selectors.search}.is-open`);
    const openNavDetails = document.querySelector('.cw-nav-details[open]');

    if (event.key === 'Escape') {
      if (openSearchElement) closeSearch();
      else if (openMenuElement) closeMenu();
      else if (openNavDetails) closeNavDetails({ restoreFocus: true });
      return;
    }

    if (openSearchElement) trapFocus(event, openSearchElement);
    else if (openMenuElement) trapFocus(event, openMenuElement);
  });

  document.addEventListener('shopify:section:load', (event) => {
    initHeader(event.target);
    initReveal(event.target);
    initTyping(event.target);
    initFooterMotion(event.target);
    destroyAnnouncements(event.target);
    initAnnouncements(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyHeader(event.target);
    destroyFooterMotion(event.target);
    destroyAnnouncements(event.target);
  });

  document.addEventListener('shopify:section:select', (event) => {
    showStickyHeader();
    initHeader(event.target);
  });

  document.addEventListener('shopify:section:reorder', (event) => {
    initHeader(event.target);
    initFooterMotion(event.target);
    initAnnouncements(event.target);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const block = event.target;
    if (!block) return;
    block.dataset.cwSelected = 'true';
    showStickyHeader();
    const isReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (typeof block.scrollIntoView === 'function') {
      block.scrollIntoView({ behavior: isReduced ? 'auto' : 'smooth', block: 'nearest' });
    }
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const block = event.target;
    if (!block) return;
    delete block.dataset.cwSelected;
  });

  document.addEventListener('visibilitychange', () => {
    announcementStates.forEach((_state, region) => {
      if (document.hidden) stopAnnouncement(region);
      else startAnnouncement(region);
    });
  });

  window.cwInitReveal = initReveal;
  initHeader();
  initReveal();
  initTyping();
  initFooterMotion();
  initAnnouncements();
})();
